import { Pipe, PipeTransform } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { LanguageService } from '../foundation/i18n/language.service';
/*
 * Raise the value exponentially
 * Takes an exponent argument that defaults to 1.
 * Usage:
 *   value | exponentialStrength:exponent
 * Example:
 *   {{ 2 | exponentialStrength:10 }}
 *   formats to: 1024
 */
@Pipe({ name: 'strict', pure: false })
export class StrictValuePipe implements PipeTransform {
  constructor(
    private translate: TranslateService,
    public languageService: LanguageService
  ) {}

  transform(value: any, stricValueName: string): string {
    const translationKey = 'strict.' + stricValueName + '.' + value;
    const translation = this.translate.instant(translationKey);

    if (translation === translationKey) {
      return value;
    }

    return translation;
  }

  strictValues(
    stricValueName: string,
    isKeyString?: boolean
  ): Map<any, string> | null {
    const translationKey = 'strict.' + stricValueName;
    const strictValues = this.translate.instant(translationKey);

    if (strictValues === translationKey) {
      return null;
    }
    return this.getStrictValueMap(strictValues, isKeyString);
  }

  asyncStrictValues(
    stricValueName: string,
    isKeyString?: boolean
  ): Observable<Map<any, string> | null> {
    const translationKey = 'strict.' + stricValueName;
    return this.translate.get(translationKey).pipe(
      map((result) => {
        if (result === translationKey) {
          return null;
        }
        return this.getStrictValueMap(result, isKeyString);
      })
    );
  }

  makeSelectListFromModel(
    values: Array<string>,
    stricValueName: string
  ): Map<string, string> {
    const translations = this.strictValues(stricValueName, true);
    const mapWithFallback = new Map<string, string>();

    for (const key of values) {
      const value = translations?.get(key);
      if (value) {
        mapWithFallback.set(key, value);
      } else {
        mapWithFallback.set(key, key);
      }
    }

    return mapWithFallback;
  }

  private getStrictValueMap(
    strictValues: any,
    isKeyString?: boolean
  ): Map<any, string> {
    const strictValueMap = new Map<any, string>();

    Object.keys(strictValues).forEach((key) => {
      if (!isKeyString) {
        if (!isNaN(Number(key))) {
          strictValueMap.set(Number(key), strictValues[key]);
        } else if (key === 'true' || key === 'false') {
          strictValueMap.set(JSON.parse(key) as boolean, strictValues[key]);
        }
      } else {
        strictValueMap.set(key, strictValues[key]);
      }
    });

    return strictValueMap;
  }
}
